import React, { useEffect, useRef } from "react";
import "./styles/OrderConfirmationScreen.css";
import { OrderConfirmationWrapper } from "./OrderConfirmationWrapper";
import { useOrderRepository } from "../../../data/repositories/OrderRepository";
import {
  ComponentState,
  useComponentStateMachine,
} from "../../shared/hooks/useComponentStateMachine";
import { LoadingView } from "../../components/loading-view/LoadingView";
import { ErrorView } from "../../components/error-view/ErrorView";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useCartContext } from "../../../contexts/CartContext";
import { useOrganizationImpersonationContext } from "../../../contexts/OrganizationImpersonationContext";
import { Order } from "../../../core/models/entities/Order";

/**
 * Fetches and displays an existing order by ID (read-only mode).
 * Separated into its own component so that the useOrder hook is always
 * called unconditionally, satisfying React's rules of hooks.
 */
function OrderConfirmationFetcher({
  organizationId,
  orderId,
}: {
  organizationId: string;
  orderId: string;
}): React.ReactElement {
  const { state, dataTransition } = useComponentStateMachine();
  const { useOrder } = useOrderRepository();

  const { data, isError, isLoading, isSuccess } = useOrder(
    +organizationId,
    +orderId,
    true // lightweight: read-only view doesn't need full design/logo graphs
  );
  dataTransition(isLoading, isSuccess, isError);

  return state === ComponentState.Succeed ? (
    <OrderConfirmationWrapper order={data!} />
  ) : state === ComponentState.Loading ? (
    <LoadingView />
  ) : (
    <ErrorView />
  );
}

export function OrderConfirmationScreen(): React.ReactElement {
  const { activeOrder, setActiveOrder } = useCartContext();
  const savedOrderRef = useRef<Order | undefined>(activeOrder);
  const navigate = useNavigate();

  // Identity switch (act-as enter/exit) while the checkout is open: whatever
  // is on screen — a local draft (id 0) or a fetched order being reviewed —
  // belongs to the OUTGOING identity. Drop the saved draft (the ref would
  // otherwise keep it alive across CartProvider's activeOrder reset) and send
  // the user to the cart, which now shows the incoming identity's items.
  const { organizationId: actAsOrgId } = useOrganizationImpersonationContext();
  const cartScope = actAsOrgId ?? 0;
  const mountedScopeRef = useRef(cartScope);
  useEffect(() => {
    if (mountedScopeRef.current === cartScope) return;
    mountedScopeRef.current = cartScope;
    savedOrderRef.current = undefined;
    setActiveOrder(undefined);
    navigate("/order/place", { replace: true });
  }, [cartScope]);

  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("orderId");
  const organizationId = searchParams.get("orgId");

  // Keep the ref in sync whenever activeOrder has a value.
  // This preserves the draft order across React StrictMode's
  // simulated unmount/remount cycle (which runs the cleanup effect
  // and clears activeOrder from context, but does NOT destroy refs).
  if (activeOrder) {
    savedOrderRef.current = activeOrder;
  }
  const effectiveOrder = activeOrder ?? savedOrderRef.current;

  useEffect(() => {
    // Clear active order when unmounting the confirmation screen
    // This ensures that when leaving the page, the cart total in the nav bar
    // reverts to showing only the cart items total (without packaging/delivery fees)
    return () => {
      setActiveOrder(undefined);
    };
  }, []);

  // If we already have a locally built draft order, use it directly without fetching.
  // Ensure we don't show a stale draft if the URL requests a specific committed order.
  const shouldUseActiveOrder =
    effectiveOrder &&
    (!orderId ||
      (effectiveOrder.id && effectiveOrder.id.toString() === orderId));

  if (shouldUseActiveOrder) {
    return <OrderConfirmationWrapper order={effectiveOrder} />;
  }

  if (orderId && organizationId) {
    return (
      <OrderConfirmationFetcher
        organizationId={organizationId}
        orderId={orderId}
      />
    );
  }

  return <Navigate to="/order/place" replace />;
}
